// Capturing the page the user is looking at, for a request's feedback board.
// (f-01928)
//
// Two ways to do this, and the choice matters more than it looks.
//
// `getDisplayMedia` is pixel-perfect — it is the browser's own compositor — but
// it puts a "choose what to share" dialog in front of the user on EVERY call,
// with no way to pre-grant it. On a shortcut meant to be pressed the instant
// something looks wrong, that dialog is most of the cost of the feature.
//
// So the DEFAULT rasterises our own DOM: silent, instant, no prompt. Its usual
// weakness is `backdrop-filter`, which a general-purpose screenshot tool cannot
// reproduce and which every glass card in PCP uses — but the CSS is ours, so a
// `html[data-capture]` pass swaps the blur for its solid background for the
// duration of the shot (see the block in index.css). What is left over for
// `getDisplayMedia` is the genuinely unreachable: a native <select> popup, a
// cross-origin iframe, a playing video. Those get **Retake**.
//
// Everything here returns `null` rather than throwing on failure. A screenshot is
// an enhancement to filing a request; a capture that did not work must never be
// the reason the request was not filed.

import { domToBlob } from 'modern-screenshot';

/** The attribute the capture-mode stylesheet keys off. */
const CAPTURE_ATTR = 'data-capture';

/**
 * Anything carrying this attribute is hidden for the duration of a capture.
 *
 * For chrome that is ABOUT taking the shot rather than part of the page: the
 * launcher's own viewfinder flash, and an open topbar tray, which is on screen
 * only because the user was on their way to press the button.
 */
export const CAPTURE_HIDE_ATTR = 'data-capture-hide';

/**
 * The longest edge we keep.
 *
 * Matches the top rung of FEEDBACK_IMG_LADDER: the board serves screenshots as
 * imgproxy renditions and imgproxy never enlarges, so pixels past this rung cost
 * upload time and disk and are never served to anyone.
 */
const MAX_EDGE = 3840;

/** A 2× grab of a large monitor is already past MAX_EDGE; 3× is pure waste. */
const MAX_SCALE = 2;

/** Belt-and-braces against a pathologically tall page. Browsers refuse a canvas
 *  past roughly this on any one side, and a refusal here would be a thrown error
 *  instead of a screenshot. */
const MAX_CANVAS_PX = 16_384;

/** Filename for a capture of `route`, e.g. "page-tickets-4821-2026-08-29.png". */
function captureName(route: string): string {
  const slug = route.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 60).toLowerCase();
  const day = new Date().toISOString().slice(0, 10);
  return `page-${slug || 'pcp'}-${day}.png`;
}

/** The scale that keeps the long edge inside MAX_EDGE — never enlarging past the
 *  device's own pixel ratio, and shrinking a very tall page rather than refusing. */
function captureScale(w: number, h: number): number {
  const longest = Math.max(w, h);
  if (!longest) return 1;
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_SCALE);
  return Math.min(dpr, MAX_EDGE / longest);
}

/**
 * Marks an element the browser is currently positioning against the VIEWPORT
 * rather than the document — see `pinViewportElements`.
 */
const CAPTURE_PIN_ATTR = 'data-capture-pin';

/**
 * Tag every element the browser currently positions against the viewport, and
 * return the undo.
 *
 * This is what makes a VIEWPORT capture possible at all. A rasteriser serialises
 * the DOM and re-renders it from the element's own origin, with no notion of
 * scroll — so the window the user is looking at is reached by translating the
 * clone up by `scrollY`. But that translate applies to `position: fixed` and
 * stuck `position: sticky` elements too, and they are precisely the things that
 * are NOT supposed to move: the sidebar, the topbar, the mobile tab bar all slid
 * off the top of the shot with everything else.
 *
 * The fix is to translate them back by the same amount. Which elements those are
 * is read from `getComputedStyle` rather than from a list of our own selectors —
 * a list would silently miss the next fixed thing anybody adds, and the whole
 * failure mode here is "silently missing".
 */
function pinViewportElements(): () => void {
  const pinned: Element[] = [];
  for (const el of document.querySelectorAll('*')) {
    const pos = getComputedStyle(el).position;
    if (pos === 'fixed' || pos === 'sticky') {
      el.setAttribute(CAPTURE_PIN_ATTR, '');
      pinned.push(el);
    }
  }
  return () => { for (const el of pinned) el.removeAttribute(CAPTURE_PIN_ATTR); };
}

/**
 * Rasterise what the user can actually see — the viewport, at its current scroll
 * position.
 *
 * Three things have to line up, and all three are verified against a real render
 * rather than assumed:
 *
 *   1. The clone is translated up and left by the scroll offset, so the visible
 *      window lands at the origin of an image sized to the viewport.
 *   2. Everything the browser pins to the viewport is translated BACK by the same
 *      amount, so the sidebar, topbar and tab bar stay where the user sees them
 *      instead of riding the translate off-screen (see `pinViewportElements`).
 *   3. Capture mode pins the properties that would otherwise render differently
 *      in a detached copy — see the `html[data-capture]` block in index.css.
 *
 * Where this cannot win — a native <select> popup, a cross-origin iframe, a
 * playing video, none of which exist in a serialised DOM at all — the sheet
 * offers **Retake**, which escalates to a true screen capture.
 */
export async function capturePage(): Promise<File | null> {
  if (typeof document === 'undefined') return null;
  const root = document.documentElement;
  const width = root.clientWidth;
  const height = root.clientHeight;
  if (!width || !height) return null;

  const scrollX = Math.round(window.scrollX);
  const scrollY = Math.round(window.scrollY);

  // Consumed by the capture-mode stylesheet's counter-translate. Set as custom
  // properties so the rule itself stays in CSS with the rest of capture mode.
  root.style.setProperty('--capture-pin-x', `${scrollX}px`);
  root.style.setProperty('--capture-pin-y', `${scrollY}px`);
  const unpin = pinViewportElements();
  root.setAttribute(CAPTURE_ATTR, '');
  try {
    const blob = await domToBlob(root, {
      width,
      height,
      scale: captureScale(width, height),
      type: 'image/png',
      maximumCanvasSize: MAX_CANVAS_PX,
      // The page's own ground, so a capture is never composited onto transparent
      // black wherever the app did not paint.
      backgroundColor: getComputedStyle(root).getPropertyValue('--bg-primary')?.trim() || '#ffffff',
      // Never rasterise the capture machinery itself. `filter` runs per node and
      // returning false drops the subtree with it.
      filter: (node) => !(node instanceof Element && node.hasAttribute(CAPTURE_HIDE_ATTR)),
      // Fonts are already loaded and same-origin; re-fetching every face and
      // inlining it as a data URI is the single slowest thing this library does by
      // default, and buys nothing when the document being cloned is this one.
      font: false,
      // Bring the scrolled window to the origin. `width`/`height` above clip to
      // the viewport, so what survives is exactly what was on screen.
      style: {
        transform: `translate(${-scrollX}px, ${-scrollY}px)`,
        transformOrigin: 'top left',
      },
    });
    if (!blob) return null;
    return new File([blob], captureName(window.location.pathname), { type: 'image/png' });
  } catch {
    // A rasteriser failure is not a reason to lose the request being filed.
    return null;
  } finally {
    root.removeAttribute(CAPTURE_ATTR);
    unpin();
    root.style.removeProperty('--capture-pin-x');
    root.style.removeProperty('--capture-pin-y');
  }
}

/** Whether the true-screenshot escalation can be offered at all. */
export function canRetakeWithDisplayMedia(): boolean {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getDisplayMedia === 'function';
}

/**
 * A TRUE screenshot, via the browser's own screen capture. The **Retake**
 * escalation — never the default, because it prompts every single time.
 *
 * `preferCurrentTab` makes Chrome pre-select this tab, reducing the dialog to one
 * click; other browsers show their full picker, which still works, just with more
 * steps. Must be called from a user gesture.
 *
 * The track is stopped in a `finally`: leaving it open leaves the browser's
 * "sharing your screen" indicator up, which is alarming and entirely pointless
 * once we have the one frame we wanted.
 */
export async function captureViaDisplayMedia(): Promise<File | null> {
  if (!canRetakeWithDisplayMedia()) return null;
  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
      // Non-standard but widely supported in Chromium; ignored elsewhere.
      preferCurrentTab: true,
    } as DisplayMediaStreamOptions);

    const track = stream.getVideoTracks()[0];
    if (!track) return null;

    const bitmap = await grabFrame(track, stream);
    if (!bitmap) return null;

    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    return new File([blob], captureName(window.location.pathname), { type: 'image/png' });
  } catch {
    // Includes the user simply cancelling the picker, which is not an error.
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}

/**
 * One frame from a live video track.
 *
 * `ImageCapture.grabFrame()` where it exists (Chromium); everywhere else, play the
 * stream into a detached <video> and wait for a frame to actually arrive —
 * `loadedmetadata` fires before there are pixels, so grabbing there yields black.
 */
async function grabFrame(track: MediaStreamTrack, stream: MediaStream): Promise<ImageBitmap | null> {
  const Capture = (window as unknown as { ImageCapture?: new (t: MediaStreamTrack) => { grabFrame: () => Promise<ImageBitmap> } }).ImageCapture;
  if (Capture) {
    try { return await new Capture(track).grabFrame(); } catch { /* fall through to the video path */ }
  }

  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  try {
    await video.play();
    await new Promise<void>((resolve) => {
      if ('requestVideoFrameCallback' in video) {
        (video as HTMLVideoElement & { requestVideoFrameCallback: (cb: () => void) => void })
          .requestVideoFrameCallback(() => resolve());
      } else {
        // No frame callback: one rAF after play() is the best signal available.
        requestAnimationFrame(() => resolve());
      }
    });
    if (!video.videoWidth || !video.videoHeight) return null;
    return await createImageBitmap(video);
  } catch {
    return null;
  } finally {
    video.pause();
    video.srcObject = null;
  }
}
